import { BaseApiService } from '../services/BaseApiService';
import { sharedTokenRefresh } from '../services/tokenRefresh';
import { debugError } from '../utils/debug';
import { ExpenseGroupModel } from '../types/case';

export interface ValidateTokenResponse {
  success: boolean;
  message?: string;
  caseId?: string;
  expiresAt?: string;
}

export interface GetExpensesResponse {
  success: boolean;
  message?: string;
  caseId?: string;
  caseNumber?: string;
  partyAName?: string;
  partyBName?: string;
  expenseGroups?: ExpenseGroupModel[];
}

export interface SaveExpensesResponse {
  success: boolean;
  message: string;
}

class ClientExpenseApi extends BaseApiService {
  constructor() {
    super(sharedTokenRefresh);
  }

  private pathTokenSeg(token: string): string {
    return encodeURIComponent(token);
  }

  async validateToken(token: string): Promise<ValidateTokenResponse> {
    try {
      const response = await this.postJson(
        `/v1/client/expenses/t/${this.pathTokenSeg(token)}/validate`,
        {},
        false
      );
      return await this.handleResponse<ValidateTokenResponse>(response);
    } catch (error) {
      debugError('Error validating client expense token:', error);
      throw error;
    }
  }

  async getExpenses(token: string): Promise<GetExpensesResponse> {
    try {
      const response = await this.postJson(`/v1/client/expenses/t/${this.pathTokenSeg(token)}`, {}, false);
      return await this.handleResponse<GetExpensesResponse>(response);
    } catch (error) {
      debugError('Error getting client expenses:', error);
      throw error;
    }
  }

  /**
   * ConcurrentCaseEditGuard A2: best-effort presence heartbeat so the attorney editing the same
   * case in the wizard sees a live "a client is entering expenses" indicator. Fire-and-forget --
   * swallows all errors (presence must never disrupt the client's editing).
   */
  async heartbeat(token: string): Promise<void> {
    try {
      await this.postJson(`/v1/client/expenses/t/${this.pathTokenSeg(token)}/heartbeat`, {}, false);
    } catch (error) {
      debugError('Error sending client expense heartbeat:', error);
    }
  }

  async saveExpenses(token: string, expenseGroups: ExpenseGroupModel[], submit: boolean = false): Promise<SaveExpensesResponse> {
    try {
      const response = await this.postJson(
        `/v1/client/expenses/t/${this.pathTokenSeg(token)}/save`,
        {
          expenseGroups,
          submit
        },
        false
      );
      return await this.handleResponse<SaveExpensesResponse>(response);
    } catch (error) {
      debugError('Error saving client expenses:', error);
      throw error;
    }
  }
}

export const clientExpenseApi = new ClientExpenseApi();

